import { userFacingStringFromMessageThenReason } from "@Front/Utils/httpValidationErrorUserMessage";
import type { HttpErrorPayload } from "../types";

export function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null;
}

export function isHttpErrorPayload(value: unknown): value is HttpErrorPayload {
	if (!isRecord(value)) {
		return false;
	}
	return (
		"http_status" in value ||
		"message" in value ||
		"code" in value ||
		"response" in value ||
		"request" in value
	);
}

function parseResponseData(responseData: unknown): { message: string | null; code: string | null } {
	if (typeof responseData === "string") {
		return { message: responseData, code: null };
	}
	if (!isRecord(responseData)) {
		return { message: null, code: null };
	}
	const code = typeof responseData["code"] === "string" ? responseData["code"] : null;
	const fromBody = userFacingStringFromMessageThenReason(responseData);
	return { message: fromBody ?? code, code };
}

function readHttpErrorTopLevel(error: HttpErrorPayload): {
	code: string | null;
	message: string | null;
	status: number | null;
} {
	const code = isRecord(error) && typeof error["code"] === "string" ? error["code"] : null;
	const message = typeof error.message === "string" ? error.message : null;
	const status = typeof error.http_status === "number" ? error.http_status : null;
	return { code, message, status };
}

function mergeResponseDataIntoFields(
	fields: { code: string | null; message: string | null; status: number | null },
	response: NonNullable<HttpErrorPayload["response"]>,
): void {
	if (!isRecord(response)) {
		return;
	}
	if (typeof response.status === "number") {
		fields.status = response.status;
	}
	const parsed = parseResponseData(response.data);
	if (parsed.code) {
		fields.code = parsed.code;
	}
	if (!fields.message && parsed.message) {
		fields.message = parsed.message;
	}
}

function coalesceMessageWithCode(message: string | null, code: string | null): string | null {
	if (message) {
		return message;
	}
	return code;
}

function readRequestId(error: unknown): string | null {
	if (!isRecord(error)) {
		return null;
	}
	const requestId = error["request_id"];
	return typeof requestId === "string" && requestId.length > 0 ? requestId : null;
}

function getMessageFromHttpPayload(error: HttpErrorPayload): ExtractedErrorMessage {
	const fields = readHttpErrorTopLevel(error);
	if (error.response && isRecord(error.response)) {
		mergeResponseDataIntoFields(fields, error.response);
	}
	if (!fields.code && isRecord(error) && typeof error["code"] === "string") {
		fields.code = error["code"];
	}
	const message = coalesceMessageWithCode(fields.message, fields.code);
	const finalMessage =
		message ?? (error.request && !error.response ? "Network error" : null);
	return { message: finalMessage, status: fields.status, code: fields.code, requestId: readRequestId(error) };
}

export type ExtractedErrorMessage = {
	message: string | null;
	status: number | null;
	code: string | null;
	/** Present ⇔ the server actually responded (network failures never carry a request id). */
	requestId: string | null;
};

/**
 * Extrait un message d'erreur depuis différents formats de réponse
 */
export function extractErrorMessage(error: unknown): ExtractedErrorMessage {
	if (isHttpErrorPayload(error)) {
		return getMessageFromHttpPayload(error);
	}
	if (typeof error === "string") {
		return { message: error, status: null, code: null, requestId: null };
	}
	if (error instanceof Error) {
		return { message: error.message, status: null, code: null, requestId: null };
	}
	if (isRecord(error)) {
		const parsed = parseResponseData(error);
		const status = typeof error["http_status"] === "number" ? error["http_status"] : null;
		return {
			message: parsed.message,
			status,
			code: parsed.code,
			requestId: readRequestId(error),
		};
	}
	return { message: null, status: null, code: null, requestId: null };
}
